import { useEffect, useRef } from "react";
import Cookies from "js-cookie";
import {  useAppDispatch } from "hooks/redux";
import {
    setTickerBlocksSnapshot,
    addTickerBlock,
    removeTickerBlock,
    setTickerBlocksLoading,
} from "store/reducers/SettingsSlice";
import Success from "utils/Notifications/Success";
import Error from "utils/Notifications/Error";
import { setPendingTickerCommand } from "store/reducers/TabsSlice";

export interface TickerBlockItem {
    group_name: string;
    ticker_name: string;
    blocked_by: string;
    blocked_at: string;
}

export interface TickerBlockUpdate {
    type: "update";
    action: "block" | "unblock";
    group_name: string;
    ticker_name: string;
    blocked_by: string;
    blocked_at?: string;
}

export interface TickerBlockSnapshot {
    type: "snapshot";
    items: TickerBlockItem[];
}

export type TickerBlockMessage = TickerBlockSnapshot | TickerBlockUpdate;

export default function TickerBlocksSockets() {
    const dispatch = useAppDispatch();


    const socketRef = useRef<WebSocket | null>(null);
    const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    const buildSocketUrl = (): string => {
        const token = Cookies.get("access");
        if (!token) return "";

        const wsProtocol = process.env.REACT_APP_SOCKET_SECURE || "wss://";
        const domain = process.env.REACT_APP_BASE_API_DOMAIN || "";

        return `${wsProtocol}${domain}/ws/ticker_blocks/?token=${encodeURIComponent(token)}`;
    };

    const createSocket = () => {
        const token = Cookies.get("access");
        if (!token) {
            console.warn("Нет токена для ticker_blocks socket");
            return null;
        }

        const url = buildSocketUrl();
        if (!url) return null;

        const socket = new WebSocket(url);

        socket.onopen = () => {
            // Success("[ticker_status] Соединение установлено");
            dispatch(setTickerBlocksLoading(true));
            if (reconnectTimeoutRef.current) {
                clearTimeout(reconnectTimeoutRef.current);
                reconnectTimeoutRef.current = null;
            }
        };

        socket.onmessage = (event) => {
            try {
                const data: TickerBlockMessage = JSON.parse(event.data);

                if (data.type === "snapshot") {
                    dispatch(setTickerBlocksSnapshot(data.items));
                } else if (data.type === "update") {
                    dispatch(
                        setPendingTickerCommand({
                            ticker_name: data.ticker_name,
                            pending: false,
                        }),
                    );
                    if (data.action === "block") {
                        const blockItem: TickerBlockItem = {
                            group_name: data.group_name,
                            ticker_name: data.ticker_name,
                            blocked_by: data.blocked_by,
                            blocked_at:
                                data.blocked_at || new Date().toISOString(),
                        };
                        dispatch(addTickerBlock(blockItem));
                    } else if (data.action === "unblock") {
                        dispatch(
                            removeTickerBlock({
                                group_name: data.group_name,
                                ticker_name: data.ticker_name,
                            }),
                        );
                    }
                }
            } catch (err) {
                Error("[TickerBlocks] Parse error");
                console.error("[TickerBlocks] Parse error:", err);
            } finally {
                dispatch(setTickerBlocksLoading(false));
            }
        };

        socket.onerror = (err) => {
            console.error("[TickerBlocks] Socket error:", err);
            dispatch(setTickerBlocksLoading(false));
        };

        socket.onclose = (event) => {
            // Авто-реконнект с экспоненциальной задержкой
            if (!reconnectTimeoutRef.current) {
                const attemptReconnect = () => {
                    socketRef.current = createSocket();
                };

                reconnectTimeoutRef.current = setTimeout(
                    attemptReconnect,
                    3000,
                );
            }
        };

        return socket;
    };

    useEffect(() => {
        if (socketRef.current?.readyState === WebSocket.OPEN) {
            socketRef.current.close();
        }

        socketRef.current = createSocket();

        return () => {
            if (reconnectTimeoutRef.current) {
                clearTimeout(reconnectTimeoutRef.current);
                reconnectTimeoutRef.current = null;
            }
            if (socketRef.current?.readyState === WebSocket.OPEN) {
                socketRef.current.close();
            }
            dispatch(setTickerBlocksLoading(false));
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [Cookies.get("access")]);

    useEffect(() => {
        (window as any).tickerBlocksSocket = {
            reconnect: () => {
                if (socketRef.current?.readyState === WebSocket.OPEN) {
                    socketRef.current.close();
                }
                socketRef.current = createSocket();
            },

            getStatus: () => ({
                readyState: socketRef.current?.readyState,
                isConnected: socketRef.current?.readyState === WebSocket.OPEN,
            }),
        };

        return () => {
            delete (window as any).tickerBlocksSocket;
        };
    }, []);

    return null;
}
